import * as React from 'react'

import { CascadeSurface } from '~/client/graph'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { IResourceControlCascade } from '~/client/src/shared/resources/domain/resourceControls'

interface IProps {
  isOpen: boolean
  // The server-computed blast radius. The dialog is only opened with a non-empty
  // cascade — a zero-affected result is committed directly without confirmation.
  cascade: IResourceControlCascade | null
  isCommitting: boolean
  onConfirm: () => void
  onCancel: () => void
}

// Turns a permit type's raw removals into plain-language lines the admin can read,
// grouped by where the resource was referenced. Product terms: a dynamic filter is a
// "conditional filter" and a conditional-rule trigger is a "conditional field". When
// the whole entry is deleted it reads as removed; when it survives, the resource is
// only no longer selectable there. Counts are by distinct affected field: a bulk
// change can remove several resources from the same field, which is one field, not
// several. Conditional-rule triggers carry no fieldId, so each removal falls back to a
// per-record key (best effort — they stay per-reference until the server sends an id).
function describeRemovals(
  removals: IResourceControlCascade['affectedPermitTypes'][number]['removals'],
): Array<{ key: string; text: string }> {
  const selectableInFields = new Set<string>()
  const excluded = new Set<string>()
  const conditionalFiltersPruned = new Set<string>()
  const conditionalFiltersRemoved = new Set<string>()
  const conditionalFieldsPruned = new Set<string>()
  const conditionalFieldsRemoved = new Set<string>()
  let hasOtherReferences = false

  const list = removals ?? []
  for (let index = 0; index < list.length; index += 1) {
    const removal = list[index]
    const fieldKey = removal.fieldId ?? `idx:${index}`
    switch (removal.surface) {
      case CascadeSurface.AllowToSelect:
        selectableInFields.add(fieldKey)
        break
      case CascadeSurface.Exclusion:
        excluded.add(fieldKey)
        break
      case CascadeSurface.DynamicFilter:
        if (removal.entryOrRuleDeleted) conditionalFiltersRemoved.add(fieldKey)
        else conditionalFiltersPruned.add(fieldKey)
        break
      case CascadeSurface.ConditionalRule:
        if (removal.entryOrRuleDeleted) conditionalFieldsRemoved.add(fieldKey)
        else conditionalFieldsPruned.add(fieldKey)
        break
      default:
        hasOtherReferences = true
    }
  }

  // Each non-empty bucket emits one line, in this fixed display order: removed
  // entries first, then "no longer selectable", then exclusions.
  const { translator: t } = Localization
  const buckets: Array<[string, number, (count: number) => string]> = [
    [
      'fieldsRemoved',
      conditionalFieldsRemoved.size,
      t.cascadeConditionalFieldsRemoved,
    ],
    [
      'filtersRemoved',
      conditionalFiltersRemoved.size,
      t.cascadeConditionalFiltersRemoved,
    ],
    [
      'selectable',
      selectableInFields.size,
      t.cascadeNoLongerSelectableInFields,
    ],
    [
      'filtersPruned',
      conditionalFiltersPruned.size,
      t.cascadeNoLongerSelectableInConditionalFilters,
    ],
    [
      'fieldsPruned',
      conditionalFieldsPruned.size,
      t.cascadeNoLongerSelectableInConditionalFields,
    ],
    ['excluded', excluded.size, t.cascadeRemovedFromExclusion],
  ]
  const lines = buckets
    .filter(([, count]) => count > 0)
    .map(([key, count, render]) => ({ key, text: render(count) }))

  // An unknown future surface, or an empty removals list, must still show a reason so
  // an affected permit type is never listed with no explanation under its name.
  if (hasOtherReferences || lines.length === 0) {
    lines.push({ key: 'other', text: t.cascadeOtherReferencesRemoved })
  }
  return lines
}

function ResourceControlCascadeConfirmDialog({
  isOpen,
  cascade,
  isCommitting,
  onConfirm,
  onCancel,
}: IProps) {
  const affectedPermitTypes = cascade?.affectedPermitTypes ?? []
  // Prefer the server's authoritative count over the array length so the header stays
  // correct even if the list is ever capped; falls back to the length when absent.
  const affectedCount =
    cascade?.affectedPermitTypeCount ?? affectedPermitTypes.length

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onCancelClicked={onCancel}
      onDoneClicked={onConfirm}
      title={Localization.translator.confirmResourceControlChangesTitle}
      doneTitle={Localization.translator.confirm}
      loading={isCommitting}
    >
      <div className="tw:flex tw:flex-col tw:gap-3">
        <p className="tw:text-body-md tw:text-destructive">
          {Localization.translator.resourceControlChangeAffectsWorkflows(
            affectedCount,
          )}
        </p>
        <ul className="tw:flex tw:max-h-64 tw:flex-col tw:gap-2 tw:overflow-y-auto">
          {affectedPermitTypes.map(permitType => (
            <li
              key={permitType.permitTypeId}
              className="tw:flex tw:flex-col tw:gap-0.5 tw:rounded-md tw:bg-muted tw:px-3 tw:py-2"
            >
              <span className="tw:text-body-md tw:font-medium tw:text-foreground">
                {permitType.permitTypeName ?? permitType.permitTypeType}
              </span>
              {describeRemovals(permitType.removals).map(line => (
                <span
                  key={line.key}
                  className="tw:text-body-sm tw:text-muted-foreground"
                >
                  {line.text}
                </span>
              ))}
            </li>
          ))}
        </ul>
      </div>
    </ConfirmDialog>
  )
}

export default ResourceControlCascadeConfirmDialog
